import { useEffect, useRef } from 'react';
import { PencilIcon, PlusIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Entry } from '../../services/api/timeEntry';
import { formatDayMonthShort, formatWeekdayShort } from '../../utils/dateFormat';

interface CellEntriesPopoverProps {
  entries: Entry[];
  projectName: string;
  date: string;
  isLocked: boolean;
  /**
   * MKS-536 — the cell is closed by the PROJECT-MONTH lock only (not by a
   * submitted timesheet), and the hours-neutral move exemption is on. Editing
   * an entry is then still offered — moving hours around inside the month
   * leaves its total alone — while Add and Delete stay hidden, because those
   * two change the total and still need a backdated-hours request. False while
   * the release flag is off, which is today's behaviour exactly.
   */
  lockedEditAllowed?: boolean;
  position: { top: number; left: number };
  onEdit?: (entry: Entry) => void;
  onDelete?: (entry: Entry) => void;
  onAdd?: () => void;
  onClose: () => void;
  readOnly?: boolean;
}

export function CellEntriesPopover({
  entries,
  projectName,
  date,
  isLocked,
  lockedEditAllowed = false,
  position,
  onEdit,
  onDelete,
  onAdd,
  onClose,
  readOnly = false,
}: CellEntriesPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Adjust position to fit within viewport
  useEffect(() => {
    if (!popoverRef.current) return;
    const el = popoverRef.current;
    const rect = el.getBoundingClientRect();
    const padding = 8;

    if (rect.right > window.innerWidth - padding) {
      el.style.left = `${Math.max(padding, window.innerWidth - rect.width - padding)}px`;
    }
    if (rect.bottom > window.innerHeight - padding) {
      el.style.top = `${Math.max(padding, window.innerHeight - rect.height - padding)}px`;
    }
    if (rect.left < padding) {
      el.style.left = `${padding}px`;
    }
    if (rect.top < padding) {
      el.style.top = `${padding}px`;
    }
  }, [position]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Delay adding listener to avoid immediate close from the click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Format date for display — MKS-351: "Mon 20 Nov", English, no browser locale.
  const formattedDate = `${formatWeekdayShort(date)} ${formatDayMonthShort(date)}`;

  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[240px] max-w-[320px]"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <div className="font-medium text-gray-900 text-sm truncate" title={projectName}>
          {projectName}
        </div>
        <div className="text-xs text-gray-500">{formattedDate}</div>
      </div>

      {/* Entries list */}
      <div className="max-h-[200px] overflow-y-auto">
        {entries.length === 0 ? (
          <div className="px-3 py-4 text-center text-gray-500 text-sm">No entries yet</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <li key={entry.id} className="px-3 py-2 hover:bg-gray-50 group">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{entry.hours}h</span>
                      {entry.task && (
                        <span className="text-xs text-gray-500 truncate">{entry.task}</span>
                      )}
                    </div>
                    {entry.state === 'rejected' && entry.rejectionReason ? (
                      <div className="text-xs text-red-600 truncate" title={entry.rejectionReason}>
                        <span className="font-medium">Rejected:</span> {entry.rejectionReason}
                      </div>
                    ) : entry.description ? (
                      <div className="text-xs text-gray-500 truncate" title={entry.description}>
                        {entry.description}
                      </div>
                    ) : null}
                  </div>
                  {!readOnly &&
                    (!isLocked || lockedEditAllowed) &&
                    (entry.state === 'open' || entry.state === 'rejected') && (
                      <div className="flex items-center gap-0.5">
                        {onEdit && (
                          <button
                            onClick={() => onEdit(entry)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title={
                              isLocked
                                ? 'Move entry (the month is locked for adding hours)'
                                : 'Edit entry'
                            }
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {/* MKS-536 — deleting removes hours from the month, which
                            the lock still refuses; only the move is exempt. */}
                        {onDelete && !isLocked && (
                          <button
                            onClick={() => onDelete(entry)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete entry"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  {entry.state === 'rejected' && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                      title={entry.rejectionReason || undefined}
                    >
                      rejected
                    </span>
                  )}
                  {(isLocked || (entry.state !== 'open' && entry.state !== 'rejected')) && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        entry.state === 'submitted'
                          ? 'bg-yellow-100 text-yellow-700'
                          : entry.state === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {entry.state}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {entries.length > 0 && (
            <>
              <DocumentDuplicateIcon className="w-3 h-3 inline mr-1" />
              {entries.length} {entries.length === 1 ? 'entry' : 'entries'} &middot; {totalHours}h
              total
            </>
          )}
        </div>
        {!readOnly && !isLocked && onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        )}
      </div>
    </div>
  );
}
